// @flow
import {
  takeEvery, put, call, take
} from 'redux-saga/effects';

import {
  getArrayResource,
  createResource,
  updateResource,
  deleteResource
} from '@redux/components/ApiRequest/actions';

import {
  DISMISS_CONFIRMATION,
  RESPONSE_CONFIRMED,
  CONFIRM_DELETE
} from '@components/ConfirmationDialog/redux/constants';
import { displayConfirmation } from '@components/ConfirmationDialog/redux/actions';
import type { Action as DismissAction } from '@components/ConfirmationDialog/types';

import { mainApiUrl } from '@lib';
import {
  WORK_EXPERIENCES_REQUEST,
  WORK_EXPERIENCE_CREATE,
  WORK_EXPERIENCE_CREATED,
  WORK_EXPERIENCE_UPDATE,
  WORK_EXPERIENCE_UPDATED,
  WORK_EXPERIENCE_DELETE,
  WORK_EXPERIENCE_DELETED
} from './constants';

import {
  updateWorkExperiences,
  errorWorkExperiences,
  createdWorkExperience,
  errorCreateWorkExperience,
  updatedWorkExperience,
  errorUpdateWorkExperience,
  deletedWorkExperience,
  errorDeletedWorkExperience
} from './actions';

import type {
  Action, CreateAction, UpdateAction, DeleteAction
} from '../types';

export const getResourceAction = () =>
  getArrayResource(`${mainApiUrl}/v1/candidate_jobs`, updateWorkExperiences, errorWorkExperiences);

export const createResourceAction = (action: CreateAction) =>
  createResource(
    `${mainApiUrl}/v1/candidate_jobs`,
    { candidate_job: action.data || {} },
    createdWorkExperience,
    errorCreateWorkExperience
  );

export const updateResourceAction = (action: UpdateAction) =>
  updateResource(
    action.id,
    `${mainApiUrl}/v1/candidate_jobs/${action.id}`,
    { candidate_job: action.data || {} },
    updatedWorkExperience,
    errorUpdateWorkExperience
  );

export const deleteResourceAction = (action: DeleteAction) =>
  deleteResource(
    action.id,
    `${mainApiUrl}/v1/candidate_jobs/${action.id}`,
    deletedWorkExperience,
    errorDeletedWorkExperience
  );

export function* deleteResourceFlow(action: DeleteAction): Generator<Function, void, void> {
  yield put(displayConfirmation(CONFIRM_DELETE));

  const dismissedAction: ?DismissAction = yield take(DISMISS_CONFIRMATION);

  if (dismissedAction && dismissedAction.response === RESPONSE_CONFIRMED) {
    yield put(deleteResourceAction(action));
  } else {
    yield put(errorDeletedWorkExperience(action.id, new Error()));
  }
}

export function* workExperienceFlow(action: Action): Generator<Function, void, void> {
  switch (action.type) {
    case WORK_EXPERIENCES_REQUEST:
      yield put(getResourceAction());
      break;

    case WORK_EXPERIENCE_CREATE:
      yield put(createResourceAction(action));
      break;

    case WORK_EXPERIENCE_UPDATE:
      yield put(updateResourceAction(action));
      break;

    case WORK_EXPERIENCE_DELETE:
      yield call(deleteResourceFlow, action);
      break;

    default:
      break;
  }
}

export function* workExperienceRefetchFlow(action: Action): Generator<Function, void, void> {
  switch (action.type) {
    case WORK_EXPERIENCE_CREATED:
    case WORK_EXPERIENCE_UPDATED:
    case WORK_EXPERIENCE_DELETED:
      yield put(getResourceAction());
      break;

    default:
      break;
  }
}

function* workExperiencesWatcher(): Generator<Function, void, Action> {
  yield takeEvery(
    [
      WORK_EXPERIENCES_REQUEST,
      WORK_EXPERIENCE_CREATE,
      WORK_EXPERIENCE_DELETE,
      WORK_EXPERIENCE_UPDATE
    ],
    workExperienceFlow
  );

  yield takeEvery(
    [WORK_EXPERIENCE_CREATED, WORK_EXPERIENCE_DELETED, WORK_EXPERIENCE_UPDATED],
    workExperienceRefetchFlow
  );
}

export default workExperiencesWatcher;
